import { useEffect, useState } from "react";
import {
  applyTranscriptionSessionEvent,
  getProgressBySessionMap,
  getProgressForSession,
  subscribeTranscriptionJobProgress,
  toUiProgress,
} from "../utils/transcriptionJobProgressStore";
import { subscribeTranscriptionSessionEvents } from "../services/websocketService";

let bridgeStarted = false;

function ensureProgressBridge() {
  if (bridgeStarted) return () => {};
  bridgeStarted = true;
  return subscribeTranscriptionSessionEvents((type, data) => {
    applyTranscriptionSessionEvent(type, data);
  });
}

ensureProgressBridge();

function progressSignature(ui) {
  if (!ui) return "";
  return [
    ui.active ? 1 : 0,
    ui.stage || "",
    ui.status || "",
    ui.percent ?? 0,
    ui.message || "",
    ui.jobId || "",
  ].join("|");
}

/**
 * Full map — prefer useSessionTranscriptionProgress for UI that only cares
 * about one visit, so other sessions' ticks do not force re-renders.
 */
export function useTranscriptionProgressBySession() {
  const [map, setMap] = useState(() => getProgressBySessionMap());

  useEffect(() => subscribeTranscriptionJobProgress(setMap), []);

  return map;
}

/**
 * Per-session progress. Only re-renders when this session's UI fields change.
 */
export function useSessionTranscriptionProgress(sessionId) {
  const sid =
    sessionId != null && String(sessionId) !== "undefined" && String(sessionId) !== "null"
      ? String(sessionId)
      : "";

  const [progress, setProgress] = useState(() =>
    toUiProgress(sid ? getProgressForSession(sid) : null),
  );

  useEffect(() => {
    if (!sid) {
      setProgress(toUiProgress(null));
      return undefined;
    }

    let lastSig = progressSignature(toUiProgress(getProgressForSession(sid)));
    setProgress(toUiProgress(getProgressForSession(sid)));

    return subscribeTranscriptionJobProgress((map) => {
      const next = toUiProgress(map[sid] || null);
      const sig = progressSignature(next);
      if (sig === lastSig) return;
      lastSig = sig;
      setProgress(next);
    });
  }, [sid]);

  return progress;
}
